import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Brain, Camera, MessageCircle, Shield, Users, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Home = () => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate('/dashboard');
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const features = [
    { icon: Camera, title: t('home_feature_image_title'), desc: t('home_feature_image_desc') },
    { icon: MessageCircle, title: t('home_feature_chat_title'), desc: t('home_feature_chat_desc') },
    { icon: Shield, title: t('home_feature_reports_title'), desc: t('home_feature_reports_desc') },
    { icon: Users, title: t('home_feature_multi_title'), desc: t('home_feature_multi_desc') },
    { icon: Brain, title: t('home_feature_smart_title'), desc: t('home_feature_smart_desc') },
    { icon: Sparkles, title: t('home_feature_always_title'), desc: t('home_feature_always_desc') }
  ];

  return (
    <div className="min-h-screen">
      <nav className="glass-navbar">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <Heart className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-2xl font-semibold tracking-tight text-slate-900">AI Vet</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login" className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900">{t('home_sign_in')}</Link>
              <Link to="/register" className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">{t('home_get_started')}</Link>
            </div>
          </div>
        </div>
      </nav>

      <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-10 pt-14 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
            <Sparkles className="h-3.5 w-3.5" />
            {t('home_badge')}
          </div>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-slate-900 md:text-5xl">
            {t('home_title_ai')} <span className="text-blue-600">{t('home_title_vet')}</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg text-slate-600">{t('home_hero_desc')}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="inline-flex items-center rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white hover:bg-blue-700">
              {t('home_cta_primary')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link to="/login" className="inline-flex items-center rounded-xl border border-slate-200 bg-white/80 px-6 py-3 font-semibold text-slate-700 hover:bg-white">
              {t('home_sign_in')}
            </Link>
          </div>
        </div>

        <div className="rounded-3xl border border-white/70 bg-white/75 p-6 shadow-2xl backdrop-blur-xl">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-slate-900 p-4 text-white">
              <p className="text-xs text-slate-300">{t('home_stat_response')}</p>
              <p className="mt-1 text-2xl font-bold">&lt; 5s</p>
            </div>
            <div className="rounded-2xl bg-blue-600 p-4 text-white">
              <p className="text-xs text-blue-100">{t('home_stat_availability')}</p>
              <p className="mt-1 text-2xl font-bold">24/7</p>
            </div>
            <div className="col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
              <p className="mb-1 text-sm font-semibold text-slate-900">{t('home_consult_flow')}</p>
              <p className="text-sm text-slate-600">{t('home_consult_flow_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">{t('home_features_title')}</h2>
          <p className="mt-2 text-slate-600">{t('home_features_desc')}</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white/85 p-5 shadow-[0_10px_30px_rgba(2,6,23,0.08)]">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600">{desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
